import React, { useEffect, useRef, useState } from 'react';
import './App.css';

import { TABS, NAV_ITEMS } from './constants/navigation';
import { NOTIFICATIONS } from './data/appData';
import LoginScreen from './components/auth/LoginScreen';
import NavItem from './components/common/NavItem';
import NotificationPanel from './components/common/NotificationPanel';
import HomeView from './views/HomeView';
import WorkoutView from './views/WorkoutView'; // Nuevo
import PlanView from './views/PlanView';
import GameView from './views/GameView';
import CommunityView from './views/CommunityView';
import ProfileView from './views/ProfileView';
import ProfileEditView from './views/ProfileEditView';
import SettingsView from './views/SettingsView';
import { GYM_RANKS } from './data/ranks';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.HOME);
  const contentRef = useRef(null);
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('sportivos-theme') || 'dark';
    }
    return 'dark';
  });
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const unreadCount = NOTIFICATIONS.filter((notification) => notification.unread).length;
  const [profileMode, setProfileMode] = useState('view');
  const [profileData, setProfileData] = useState({
    name: 'Javier Demo',
    rankId: 'athlete',
    email: 'javier@sportivos.com',
    phone: '',
    gender: 'masculino',
    avatarUrl: 'https://images.unsplash.com/photo-1558611848-73f7eb4001a1?auto=format&fit=crop&w=400&q=80',
    passwordSet: false,
  });
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Estado Global de Rutina
  const [routineState, setRoutineState] = useState({
    isActive: false,
    isPlayerOpen: false,
    routine: null,
  });

  const handleRoutineStart = (routine) => {
    setRoutineState({
      isActive: true,
      isPlayerOpen: true,
      routine: routine
    });
  };

  const handleRoutineExit = () => {
    // Aquí podriamos guardar historial antes de limpiar
    setRoutineState({
      isActive: false,
      isPlayerOpen: false,
      routine: null
    });
  };

  const handleRoutineMinimize = () => {
    setRoutineState(prev => ({ ...prev, isPlayerOpen: false }));
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('sportivos-theme', theme);
    }
  }, [theme]);

  useEffect(() => {
    if (!notificationsOpen) return;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setNotificationsOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [notificationsOpen]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== TABS.PROFILE && profileMode !== 'view') {
      setProfileMode('view');
    }
  }, [activeTab, profileMode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const isStandalone = () =>
      window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

    if (isStandalone()) {
      return undefined;
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
      setShowInstallBanner(true);
    };

    const handleAppInstalled = () => {
      setInstallPromptEvent(null);
      setShowInstallBanner(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const setThemeMode = (mode) => {
    setTheme(mode);
  };

  const handleAuthSuccess = (profileOverrides) => {
    let signupPreviewToRevoke = null;

    if (profileOverrides) {
      if (typeof profileOverrides.avatarUrl === 'string') {
        signupPreviewToRevoke = profileOverrides.avatarUrl;
      }

      setProfileData((prev) => {
        let nextAvatar = prev.avatarUrl;

        if (profileOverrides.avatarFile instanceof File) {
          if (typeof prev.avatarUrl === 'string' && prev.avatarUrl.startsWith('blob:')) {
            URL.revokeObjectURL(prev.avatarUrl);
          }
          nextAvatar = URL.createObjectURL(profileOverrides.avatarFile);
        } else if (typeof profileOverrides.avatarUrl === 'string' && profileOverrides.avatarUrl) {
          if (typeof prev.avatarUrl === 'string' && prev.avatarUrl.startsWith('blob:') && prev.avatarUrl !== profileOverrides.avatarUrl) {
            URL.revokeObjectURL(prev.avatarUrl);
          }
          nextAvatar = profileOverrides.avatarUrl;
          signupPreviewToRevoke = null;
        }

        const nextPasswordSet = profileOverrides.passwordProvided ? true : prev.passwordSet;

        return {
          ...prev,
          name: profileOverrides.name?.trim() || prev.name,
          email: profileOverrides.email?.trim() || prev.email,
          phone: profileOverrides.phone?.trim() || prev.phone || '',
          avatarUrl: nextAvatar,
          passwordSet: nextPasswordSet,
        };
      });
    }

    if (signupPreviewToRevoke && signupPreviewToRevoke.startsWith('blob:')) {
      URL.revokeObjectURL(signupPreviewToRevoke);
    }

    setIsLoggedIn(true);
    setActiveTab(TABS.HOME);
  };

  const handleProfileEdit = () => {
    setProfileMode('edit');
  };

  const handleProfileCancel = () => {
    setProfileMode('view');
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setActiveTab(TABS.HOME);
    setProfileMode('view');
    setNotificationsOpen(false);
    setShowInstallBanner(false);
    setInstallPromptEvent(null);
  };

  const handleInstallApp = async () => {
    if (!installPromptEvent) {
      return;
    }

    installPromptEvent.prompt();
    try {
      await installPromptEvent.userChoice;
    } catch (error) {
      // Ignore errors from user dismissal
    }
    setInstallPromptEvent(null);
    setShowInstallBanner(false);
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
  };

  const openSettings = () => {
    setProfileMode('view');
    setActiveTab(TABS.SETTINGS);
  };

  const closeSettings = () => {
    setActiveTab(TABS.PROFILE);
  };

  const handleProfileSave = (nextProfile) => {
    setProfileData((prev) => {
      const updatedName = nextProfile.name.trim() || prev.name;
      const updatedEmail = nextProfile.email.trim() || prev.email;
      const updatedRank = nextProfile.rankId || prev.rankId;
      const updatedGender = nextProfile.gender || prev.gender;
      const updatedPhone = typeof nextProfile.phone === 'string' ? nextProfile.phone : prev.phone;
      const nextAvatar = typeof nextProfile.avatarUrl === 'string' ? nextProfile.avatarUrl : prev.avatarUrl;
      if (prev.avatarUrl && nextAvatar && prev.avatarUrl !== nextAvatar && typeof prev.avatarUrl === 'string' && prev.avatarUrl.startsWith('blob:')) {
        URL.revokeObjectURL(prev.avatarUrl);
      }

      return {
        ...prev,
        name: updatedName,
        email: updatedEmail,
        rankId: updatedRank,
        gender: updatedGender,
        phone: updatedPhone,
        avatarUrl: nextAvatar,
        passwordSet: nextProfile.password ? true : prev.passwordSet,
      };
    });
    setProfileMode('view');
  };

  if (!isLoggedIn) {
    return <LoginScreen theme={theme} onLogin={handleAuthSuccess} />;
  }

  return (
    <div className="app-root" data-theme={theme}>
      {showInstallBanner && (
        <div className="install-banner" role="dialog" aria-live="polite">
          <div className="install-banner-content">
            <div>
              <p className="install-banner-title">Instala Sportivos Gym</p>
              <p className="install-banner-text">Accede más rápido y disfruta la experiencia completa desde tu pantalla de inicio.</p>
            </div>
            <div className="install-banner-actions">
              <button type="button" className="pill-button pill-button-ghost" onClick={dismissInstallBanner}>
                Ahora no
              </button>
              <button type="button" className="pill-button pill-button-primary" onClick={handleInstallApp} disabled={!installPromptEvent}>
                Instalar
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="app-shell">
        <header className="app-header light">
          <div className="app-header-main">
            <img className="app-logo-image" src="/logo-sportivos.png" alt="Sportivos" />
          </div>
          <div className="app-header-actions">
            <button
              type="button"
              className={unreadCount > 0 ? 'icon-button has-badge' : 'icon-button'}
              onClick={() => setNotificationsOpen(true)}
              aria-label={`Ver notificaciones${unreadCount > 0 ? `, ${unreadCount} sin leer` : ''}`}
            >
              <span className="icon-bell" aria-hidden="true">
                🔔
              </span>
              {unreadCount > 0 && (
                <span className="icon-button-badge" aria-hidden="true">
                  {unreadCount}
                </span>
              )}
              <span className="sr-only">Notificaciones</span>
            </button>
          </div>
        </header>

        <main className="app-content" ref={contentRef}>
          {activeTab === TABS.HOME && (
            <HomeView
              profile={profileData}
              onNavigateToPlan={() => setActiveTab(TABS.PLAN)}
              onNavigateToWorkout={() => setActiveTab(TABS.WORKOUT)}
            />
          )}
          {activeTab === TABS.WORKOUT && (
            <WorkoutView
              routineState={routineState}
              onStartRoutine={handleRoutineStart}
              onExitRoutine={handleRoutineExit}
              onMinimizeRoutine={handleRoutineMinimize}
            />
          )}
          {activeTab === TABS.PLAN && <PlanView />}
          {activeTab === TABS.GAME && <GameView />}
          {activeTab === TABS.COMMUNITY && <CommunityView />}
          {activeTab === TABS.PROFILE &&
            (profileMode === 'edit' ? (
              <ProfileEditView
                profile={profileData}
                ranks={GYM_RANKS}
                onCancel={handleProfileCancel}
                onSave={handleProfileSave}
              />
            ) : (
              <ProfileView
                profile={profileData}
                ranks={GYM_RANKS}
                onEdit={handleProfileEdit}
                onOpenSettings={openSettings}
              />
            ))}
          {activeTab === TABS.SETTINGS && (
            <SettingsView
              theme={theme}
              onToggleTheme={toggleTheme}
              onSetTheme={setThemeMode}
              onBack={closeSettings}
              canInstallApp={Boolean(installPromptEvent)}
              onInstallApp={handleInstallApp}
              onLogout={handleLogout}
            />
          )}
        </main>

        {/* Floating Workout Indicator (Si hay rutina activa pero no estamos en el tab PLAN/Playing) */}
        {routineState.isActive && (activeTab !== TABS.WORKOUT || !routineState.isPlayerOpen) && (
          <div className="mini-player-toast" onClick={() => { setActiveTab(TABS.WORKOUT); setRoutineState(prev => ({ ...prev, isPlayerOpen: true })); }}>
            <span className="mini-icon">💪</span>
            <div className="mini-info">
              <span className="mini-label">Entrenamiento en curso</span>
              <span className="mini-sub">Toca para volver</span>
            </div>
          </div>
        )}

        <nav className="bottom-nav light" aria-label="Navegación principal">
          {NAV_ITEMS.map((item) => (
            <NavItem
              key={item.id}
              icon={item.icon}
              label={item.label}
              active={activeTab === item.id}
              onClick={() => setActiveTab(item.id)}
            />
          ))}
        </nav>
        <NotificationPanel
          open={notificationsOpen}
          onClose={() => setNotificationsOpen(false)}
          notifications={NOTIFICATIONS}
          unreadCount={unreadCount}
        />
      </div>
    </div>
  );
}

export default App;
